import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@auth/AuthContext';
import { useGetMyMembershipQuery, useGetPracticeQuery, useGetRolesQuery } from '@api/endpoints/onboardingApi';
import { useGetMyAccessQuery } from '@api/endpoints/accessControlApi';
import { getComponentForPath, isConfigSubRoutePath } from '@lib/componentCatalog';
import { canAccessRoute, getDefaultRedirect, buildRouteMap, buildDefaultRedirects } from './roleConfig';

export interface RoleGuardProps {
  children: React.ReactNode;
  requiredGroups?: string[];
}

/**
 * Wraps routes that require specific Cognito group membership.
 * Uses dynamically loaded role definitions when available, with static fallback.
 * - Practice status "pending" → /registration-pending (practice not yet approved)
 * - Practice status "rejected" → /registration-rejected (practice was rejected)
 * - Membership status "pending" → /pending-approval (join-request pending)
 * - No recognized groups → /no-access
 * - Component_Entry_Routes admitted via the per-practice Access_Policy
 *   (`myAccess.accessibleComponentIds`, union semantics) rather than the static
 *   role-to-route map alone (Req 10.1, 10.3)
 * - Config_Sub_Routes stay restricted to Practice_Owner/Medrender_Owner (Req 10.4)
 * - Landing_Surfaces use the fixed role-default map with a no-lockout redirect
 *   via `getDefaultRedirect` (Req 22.2, 22.3, 22.5)
 */
export function RoleGuard({ children, requiredGroups }: RoleGuardProps) {
  const { user } = useAuth();
  const location = useLocation();
  const { data: membershipData, isLoading: isMembershipLoading } = useGetMyMembershipQuery();
  const { data: practiceData, isLoading: isPracticeLoading } = useGetPracticeQuery();
  const { data: rolesData, isError: isRolesError } = useGetRolesQuery();

  // The caller's effective Component access (precomputed Entry_Gate result over
  // the whole catalog) for policy-driven Component_Entry_Route gating (Req 10.1,
  // 8.3). Scoped to the caller's own clinic; skipped until the clinicId resolves.
  const clinicId = membershipData?.membership?.clinicId ?? practiceData?.[0]?.clinicId ?? '';
  const { data: myAccess, isLoading: isMyAccessLoading } = useGetMyAccessQuery(clinicId, {
    skip: !clinicId,
  });

  const userGroups = user?.groups ?? [];

  // Build dynamic maps if roles loaded, otherwise use static fallback
  let routeMap: Record<string, string[]> | undefined;
  let redirectMap: Record<string, string> | undefined;
  let recognizedRoleIds: Set<string> | undefined;

  if (rolesData?.roles && rolesData.roles.length > 0 && !isRolesError) {
    routeMap = buildRouteMap(rolesData.roles);
    redirectMap = buildDefaultRedirects(rolesData.roles);
    recognizedRoleIds = new Set(rolesData.roles.map(r => r.roleId));
  } else if (isRolesError) {
    console.warn('Failed to load role definitions, using static fallback');
  }

  // While loading membership or practice data, show nothing to avoid flash
  if (isMembershipLoading || isPracticeLoading) {
    return null;
  }

  // Check practice-level status (for practice creators whose practice is pending/rejected)
  const practice = practiceData?.[0];
  if (practice?.practiceStatus === 'pending') {
    if (location.pathname !== '/registration-pending') {
      return <Navigate to="/registration-pending" replace />;
    }
    return <>{children}</>;
  }
  if (practice?.practiceStatus === 'rejected') {
    if (location.pathname !== '/registration-rejected') {
      return <Navigate to="/registration-rejected" replace />;
    }
    return <>{children}</>;
  }

  // Check if user has a pending membership (join-request pending)
  const membership = membershipData?.membership;
  if (membership && membership.status === 'pending') {
    if (location.pathname !== '/pending-approval') {
      return <Navigate to="/pending-approval" replace />;
    }
  }

  // If requiredGroups is specified, check those specifically
  if (requiredGroups && requiredGroups.length > 0) {
    const hasAccess = requiredGroups.some((group) => userGroups.includes(group));
    if (!hasAccess) {
      return <Navigate to={getDefaultRedirect(userGroups, redirectMap)} replace />;
    }
    return <>{children}</>;
  }

  // No recognized groups → no-access (dynamic check if roles loaded, static fallback otherwise)
  const hasAnyRecognizedGroup = recognizedRoleIds
    ? userGroups.some((g) => recognizedRoleIds!.has(g))
    : userGroups.some((g) => g === 'medrender-owner' || g === 'practice-owner' || g === 'data_uploader' || g === 'standard-user');

  // No recognized groups and no membership → check for pending invite before redirecting
  if (!hasAnyRecognizedGroup && userGroups.length === 0) {
    // If there's a pending invite token, the AcceptInviteHandler will process it
    // and reload the page. Show a loading state instead of redirecting to onboarding.
    const hasPendingInvite = typeof window !== 'undefined' && localStorage.getItem('medrender_invite_token');
    if (hasPendingInvite) {
      return null; // AcceptInviteHandler will handle it
    }

    // If user has no membership at all, direct to onboarding path
    if (!membership) {
      if (location.pathname !== '/onboarding-path' && location.pathname !== '/join-practice' && location.pathname !== '/setup-practice') {
        return <Navigate to="/onboarding-path" replace />;
      }
      return <>{children}</>;
    }
    return <Navigate to="/no-access" replace />;
  }

  // Component_Entry_Routes are gated by the per-practice Access_Policy via the
  // My_Access_Resolver (union over the caller's Roles and Groups, Req 10.1).
  // The static role→route map is only the Default_Access_Policy seed, not the
  // runtime source of truth once a policy exists (Req 10.3). Landing_Surfaces
  // and any non-catalog route fall back to the static role-default map so a
  // role is never locked out of its app shell (Req 22.4, 22.5).
  const component = getComponentForPath(location.pathname);
  if (component && component.kind === 'feature') {
    // Config_Sub_Routes stay owner-only regardless of a Component's grants
    // (Req 10.4, 15.2).
    if (isConfigSubRoutePath(location.pathname)) {
      const isOwner = userGroups.includes('practice-owner') || userGroups.includes('medrender-owner');
      if (!isOwner) {
        return <Navigate to={getDefaultRedirect(userGroups, redirectMap)} replace />;
      }
      return <>{children}</>;
    }

    // Wait for the effective-access read before deciding, to avoid a flash.
    if (isMyAccessLoading) {
      return null;
    }

    // When My_Access resolved, admit iff the Component is accessible (owners and
    // Medrender_Owner are already all-access in this set). If it is unavailable
    // (no clinicId or a load error), fall back to the static map below.
    const accessibleComponentIds = myAccess?.accessibleComponentIds;
    if (accessibleComponentIds) {
      if (accessibleComponentIds.includes(component.componentId)) {
        return <>{children}</>;
      }
      return <Navigate to={getDefaultRedirect(userGroups, redirectMap)} replace />;
    }
  }

  // Landing_Surfaces, non-catalog routes, and the My_Access-unavailable fallback:
  // use the static role-default map with the existing no-access redirect.
  if (!canAccessRoute(userGroups, location.pathname, routeMap)) {
    return <Navigate to={getDefaultRedirect(userGroups, redirectMap)} replace />;
  }

  return <>{children}</>;
}
